import { ChannelType } from '@vision/ui/enums';
import type {
  ChannelDetailDto,
  LegacyThemeColorsRequestDto,
  LegacyThemeRequestDto,
  LegacyThemeResponseDto,
} from './channels.api.types';
import type { LegacyThemeColors, LegacyThemeFile, LegacyThemeFiles, LegacyThemeStylesheet } from './types';

// WhatsApp's upsert does not accept the files and colors yet (api-expectations).
const legacyThemeChannelTypes = new Set<ChannelType>([
  ChannelType.CATI,
  ChannelType.EMAIL,
  ChannelType.IVR,
  ChannelType.LINK,
  ChannelType.MOBILE,
  ChannelType.PUSH_NOTIFICATION,
  ChannelType.SMS,
  ChannelType.WEB_WIDGET,
]);

export function hasLegacyThemeFiles(channelType: ChannelType): boolean {
  return legacyThemeChannelTypes.has(channelType);
}

export const savedLegacyThemeFiles: LegacyThemeFiles = {
  cssUrl: { change: 'none', url: null },
  cssUrlReact: { change: 'none', url: null },
  favicon: { change: 'none', url: null },
  logo: { change: 'none', url: null },
};

export function fromApiLegacyTheme(data: LegacyThemeResponseDto): LegacyThemeFiles {
  return {
    cssUrl: { change: 'none', url: data.css_url },
    cssUrlReact: { change: 'none', url: data.css_url_react },
    favicon: { change: 'none', url: data.favicon_url },
    logo: { change: 'none', url: data.logo_url },
  };
}

function toApiImage(file: LegacyThemeFile): LegacyThemeRequestDto['logo_attributes'] {
  switch (file.change) {
    case 'remove':
      return { deleted: true };
    case 'upload':
      return { image: file.content, image_content_type: file.contentType, image_file_name: file.fileName };
    default:
      return undefined;
  }
}

function toApiStylesheet(file: LegacyThemeStylesheet): LegacyThemeRequestDto['css_url_attributes'] {
  switch (file.change) {
    case 'remove':
      return { deleted: true };
    case 'upload':
      return { stylesheet: file.content, stylesheet_content_type: 'text/css', stylesheet_file_name: file.fileName };
    case 'url':
      return { url: file.url };
    default:
      return undefined;
  }
}

// An omitted object keeps the saved file.
export function toApiLegacyTheme(legacyTheme: LegacyThemeFiles | null): LegacyThemeRequestDto {
  if (!legacyTheme) {
    return {};
  }

  const cssUrl = toApiStylesheet(legacyTheme.cssUrl);
  const cssUrlReact = toApiStylesheet(legacyTheme.cssUrlReact);
  const favicon = toApiImage(legacyTheme.favicon);
  const logo = toApiImage(legacyTheme.logo);

  return {
    ...(cssUrl && { css_url_attributes: cssUrl }),
    ...(cssUrlReact && { css_url_react_attributes: cssUrlReact }),
    ...(favicon && { favicon_attributes: favicon }),
    ...(logo && { logo_attributes: logo }),
  };
}

export function fromApiLegacyColors(data: ChannelDetailDto): LegacyThemeColors | null {
  if (data.object === 'web_channel') {
    return { primary: data.primary_color, secondary: data.secondary_color };
  }

  return 'primary_color' in data ? { primary: data.primary_color } : null;
}

export function toApiLegacyColors(colors: LegacyThemeColors | null): LegacyThemeColorsRequestDto {
  if (!colors) {
    return {};
  }

  return 'secondary' in colors
    ? { primary_color: colors.primary, secondary_color: colors.secondary }
    : { primary_color: colors.primary };
}
